import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, CloudOff, Loader2, RefreshCw, AlertTriangle, Heart, X } from "lucide-react";
import { useProgress } from "../context/ProgressContext";
import { useCountdown, fmtCountdown } from "../lib/useCountdown";
import { readHistory, historyAgo, HISTORY_EVENT } from "../lib/syncHistory";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import Panda from "./Panda";

const DAY = 86400000;
const TIP_KEY = "tycc_chip_tip_seen";
const staleLabel = (ms) => { const d = Math.floor(ms / DAY); return d >= 1 ? `Unsaved for ${d} day${d === 1 ? "" : "s"}` : "Unsaved for a day"; };

// Tiny header indicator so parents can trust offline progress: Saved / Saving / N changes waiting / Back online - syncing / stale (red).
const SyncChip = ({ variant = "desktop" }) => {
  const { isCloud, panda, syncStatus, retrySync, pendingChanges } = useProgress();
  const { pending, saving, sendingQueue, online, reconnectedAt, synced, oldestPendingAt, retryAt, authExpired } = syncStatus;
  const [history, setHistory] = useState(readHistory);
  const [, tick] = useState(0);
  const [open, setOpen] = useState(false);
  const [tipSeen, setTipSeen] = useState(() => localStorage.getItem(TIP_KEY) === "1");
  const left = useCountdown(retryAt);
  const recent = reconnectedAt && Date.now() - reconnectedAt < 6000;
  const staleMs = oldestPendingAt ? Date.now() - oldestPendingAt : 0;
  const stale = pending > 0 && !saving && staleMs > DAY;
  useEffect(() => {
    if (!recent) return;
    const t = setTimeout(() => tick((n) => n + 1), 6000);
    return () => clearTimeout(t);
  }, [recent, reconnectedAt]);
  useEffect(() => {
    const h = () => setHistory(readHistory());
    window.addEventListener(HISTORY_EVENT, h);
    return () => window.removeEventListener(HISTORY_EVENT, h);
  }, []);
  useEffect(() => {
    const seen = () => setTipSeen(true);
    window.addEventListener("tycc-chip-tip", seen);
    return () => window.removeEventListener("tycc-chip-tip", seen);
  }, []);

  if (!isCloud || (!panda?.name && pending === 0)) return null;

  let kind, label, Icon, cls;
  if (stale) {
    kind = "stale"; label = staleLabel(staleMs); Icon = AlertTriangle; cls = "bg-[#fde2e2] text-[#9b1c1c] hover:bg-[#fbcaca]";
  } else if (!online && pending > 0) {
    kind = "waiting"; label = `${pending} change${pending === 1 ? "" : "s"} waiting`; Icon = CloudOff; cls = "bg-[#fff3d6] text-[#8a5a00] hover:bg-[#ffe7b0]";
  } else if (!online) {
    kind = "offline"; label = "Saved on device"; Icon = CloudOff; cls = "bg-[#f1f5f9] text-[#475569]";
  } else if (saving && recent) {
    kind = "reconnecting"; label = "Back online - syncing"; Icon = Loader2; cls = "bg-[#e0f2fe] text-[#0c4a6e]";
  } else if (saving || !synced) {
    kind = "saving"; label = sendingQueue ? "Sending..." : "Saving..."; Icon = Loader2; cls = "bg-[#e0f2fe] text-[#0c4a6e]";
  } else if (pending > 0) {
    kind = "retry"; label = authExpired ? "Log in to save" : left !== null ? `Retrying in ${fmtCountdown(left)}` : `${pending} change${pending === 1 ? "" : "s"} waiting`; Icon = RefreshCw; cls = "bg-[#fff3d6] text-[#8a5a00] hover:bg-[#ffe7b0]";
  } else {
    kind = "saved"; label = "Saved"; Icon = Check; cls = "bg-[#e6f7ef] text-[#1f7a4d]";
  }

  const clickable = kind === "retry" || kind === "waiting" || kind === "stale" || (kind === "saved" && history.length > 0);
  const spin = Icon === Loader2 ? "animate-spin" : "";
  const title = kind === "saved" ? "Tap to see what was saved recently" : clickable ? "Tap to see what is still unsaved" : label;
  const mobile = variant === "mobile";
  const button = (
    <button
      type="button"
      disabled={!clickable}
      title={title}
      aria-label={label}
      className={`inline-flex items-center gap-1 rounded-full font-bold transition-colors ${mobile ? "h-8 px-2 text-[11px]" : "px-2.5 py-1 text-xs"} ${cls} ${clickable ? "cursor-pointer" : "cursor-default"}`}
      data-testid={mobile ? "sync-chip-mobile" : "sync-chip"}
      data-state={kind}
      data-pending={pending}
      data-retry-in={left ?? undefined}
    >
      <Icon size={12} className={spin} />
      {mobile && kind === "saved" ? null : <span>{label}</span>}
    </button>
  );
  if (!clickable) return button;

  const markTip = () => { localStorage.setItem(TIP_KEY, "1"); setTipSeen(true); window.dispatchEvent(new Event("tycc-chip-tip")); };
  const showTip = !tipSeen && !open && kind !== "saved";
  const { children: items, cheers } = pendingChanges();
  const names = [...new Set(items.map((i) => i.child.panda?.name).filter(Boolean))];
  const heading = kind === "saved" ? "All saved" : stale ? "Unsaved for over a day" : online ? "Waiting to save" : "Saved on this device";
  const sub = kind === "saved" ? "Everything on this device is in the cloud." : authExpired
    ? "Your session expired. Please log in again and these will save right away."
    : stale ? `Connect to Wi-Fi so ${names.length ? names.join(" and ") + "'s" : "this"} progress isn't lost if this device is reset.`
    : online ? (left !== null ? `The last save didn't go through - retrying automatically in ${fmtCountdown(left)}.` : "The last save didn't reach the server yet.")
    : "These will upload the moment you're back online.";
  return (
    <span className="relative inline-flex">
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) markTip(); }}>
        <PopoverTrigger asChild>{button}</PopoverTrigger>
        <PopoverContent align="end" className={`w-72 rounded-2xl p-3 shadow-xl ${stale ? "border-[#fbcaca]" : kind === "saved" ? "border-[#bfe6d0]" : "border-[#ffe7b0]"}`} data-testid="pending-popover" data-stale={stale ? "true" : "false"} data-kind={kind}>
          <p className={`text-xs font-bold uppercase tracking-wider ${stale ? "text-[#9b1c1c]" : kind === "saved" ? "text-[#1f7a4d]" : "text-[#8a5a00]"}`}>{heading}</p>
          <p className="mt-1 text-xs text-gray-600" data-testid="pending-nudge">{sub}</p>
          <ul className="mt-3 space-y-3">
            {items.map(({ child, changes }) => (
              <li key={child.id} className="flex gap-2" data-testid={`pending-child-${child.id}`}>
                <Panda size={30} accessory={child.panda?.accessory} scarf={child.panda?.scarf} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-[#0c4a6e] truncate">{child.panda?.name}</p>
                  <ul className="mt-0.5 space-y-0.5">
                    {changes.map((c, i) => <li key={i} className="text-xs text-gray-700 flex items-start gap-1"><span className="mt-1.5 h-1 w-1 rounded-full bg-[#f7931e] shrink-0" />{c}</li>)}
                  </ul>
                </div>
              </li>
            ))}
            {cheers.map((q) => (
              <li key={q.localId} className="flex gap-2" data-testid={`pending-cheer-${q.localId}`}>
                <span className="w-[30px] h-[30px] rounded-full bg-[#fff0f5] text-[#e5356f] flex items-center justify-center shrink-0"><Heart size={14} fill="#e5356f" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-[#0c4a6e] truncate">{q.kind === "reply" ? `${q.child?.panda?.name || q.childName || "Child"}'s thank-you` : `Cheer for ${q.child?.panda?.name || q.childName || "a child"}`}{q.hasPhoto ? " (with photo)" : ""}</p>
                  <p className="text-xs text-gray-700 truncate">“{q.text}”</p>
                </div>
              </li>
            ))}
          </ul>
          {history.length > 0 && (
            <div className={`${items.length || cheers.length ? "mt-3 pt-3 border-t border-[#e9f9fd]" : "mt-3"}`} data-testid="sync-history">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Recently saved</p>
              <ul className="mt-1 space-y-0.5">
                {history.slice(0, 5).map((h, i) => (
                  <li key={h.at + i} className="text-xs text-gray-700 flex justify-between gap-2" data-testid={`sync-history-${i}`}>
                    <span className="truncate inline-flex items-center gap-1"><Check size={11} className="text-[#1f7a4d] shrink-0" />{h.label}</span>
                    <span className="text-gray-400 shrink-0">{historyAgo(h.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {authExpired ? (
            <Link to="/login" onClick={() => setOpen(false)} className="mt-3 w-full btn-pill bg-[#7d5fb2] hover:bg-[#6a4fa0] text-white text-sm inline-flex items-center justify-center gap-1" data-testid="pending-login">Log in again</Link>
          ) : online && kind !== "saved" && (
            <button type="button" onClick={() => { retrySync(); setOpen(false); }} className="mt-3 w-full btn-pill bg-[#1b8bc9] hover:bg-[#166fa3] text-white text-sm inline-flex items-center justify-center gap-1" data-testid="pending-retry">
              <RefreshCw size={14} /> Retry now
            </button>
          )}
        </PopoverContent>
      </Popover>
      {showTip && (
        <span role="status" className="absolute right-0 top-[calc(100%+10px)] z-50 w-64 rounded-2xl bg-[#0c4a6e] text-white text-xs shadow-xl p-3 bubble-in" data-testid="sync-chip-tip">
          <span className="absolute -top-1.5 right-6 w-3 h-3 rotate-45 bg-[#0c4a6e]" />
          <span className="flex items-start gap-2">
            <span className="flex-1 leading-snug">Your progress is safe on this device - it uploads the moment you're back online. Tap the chip for details.</span>
            <button type="button" onClick={markTip} className="p-0.5 rounded-full hover:bg-white/15" aria-label="Dismiss tip" data-testid="sync-chip-tip-close"><X size={12} /></button>
          </span>
          <button type="button" onClick={markTip} className="mt-2 rounded-full bg-white text-[#0c4a6e] font-bold px-3 py-1 text-[11px]" data-testid="sync-chip-tip-ok">Got it</button>
        </span>
      )}
    </span>
  );
};

export default SyncChip;
